import React, { useState } from 'react';
import { CalendarIcon, MapPinIcon, ExclamationTriangleIcon, EyeIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { Button } from '@/ui/components/atoms/Button';
import { PlanningExpertise } from '../../types/planning.types';
import './ExpertiseUnscheduledCard.styles.css';

interface ExpertiseUnscheduledCardProps {
  expertise: PlanningExpertise;
  onDetailClick?: () => void;
  onScheduleClick?: () => void;
}

export const ExpertiseUnscheduledCard: React.FC<ExpertiseUnscheduledCardProps> = ({
  expertise,
  onDetailClick,
  onScheduleClick
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      top: rect.bottom + 5,
      left: Math.min(rect.right - 250, window.innerWidth - 260) // Ensure tooltip stays within viewport
    });
    setShowTooltip(true);
  };

  const getCardClassName = () => {
    const classes = ['unscheduled-card'];
    
    if (expertise.urgency?.isUrgent) {
      classes.push('unscheduled-card--urgent');
    }
    
    return classes.join(' ');
  };

  const getTypeLabel = () => {
    const typeMap: Record<string, string> = {
      'EX': 'Expertise',
      'CT': 'Contre-visite',
      'ES': 'Estimation',
      'default': expertise.type
    };
    return typeMap[expertise.type] || typeMap.default;
  };

  return (
    <div className={getCardClassName()}>
      {/* Urgency indicator */}
      {expertise.urgency?.isUrgent && (
        <div className="unscheduled-card__urgency">
          <ExclamationTriangleIcon className="unscheduled-card__urgency-icon" />
          <span>Urgent - {expertise.urgency.daysSince} jours</span>
        </div>
      )}

      {/* Header */}
      <div className="unscheduled-card__header">
        <div className="unscheduled-card__type">
          {getTypeLabel()}
        </div>
        {expertise.k_id_dossier && (
          <div className="unscheduled-card__id">
            #{expertise.k_id_dossier}
          </div>
        )}
        {/* Remarks icon with tooltip */}
        {expertise.remarques_rv && (
          <div className="unscheduled-card__remarks">
            <div 
              className="unscheduled-card__remarks-icon-wrapper"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={() => setShowTooltip(false)}
            >
              <InformationCircleIcon className="unscheduled-card__remarks-icon" />
              {showTooltip && (
                <div 
                  className="unscheduled-card__remarks-tooltip"
                  style={{ 
                    top: `${tooltipPosition.top}px`, 
                    left: `${tooltipPosition.left}px` 
                  }}
                >
                  {expertise.remarques_rv}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="unscheduled-card__content">
        {/* Address */}
        {expertise.address && expertise.address.length > 0 && (
          <div className="unscheduled-card__info">
            <MapPinIcon className="unscheduled-card__info-icon" />
            <span className="unscheduled-card__info-text">
              {expertise.address.join(', ')}
            </span>
          </div>
        )}

        {/* Expert */}
        {expertise.expert && (
          <div className="unscheduled-card__info">
            <span className="unscheduled-card__info-label">Expert:</span>
            <span className="unscheduled-card__info-text">{expertise.expert}</span>
          </div>
        )}

        {/* Status */}
        <div className="unscheduled-card__info">
          <span className="unscheduled-card__info-label">Statut:</span>
          <span className="unscheduled-card__status">
            En attente de planification
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="unscheduled-card__actions">
        <Button
          variant="primary"
          size="small"
          onClick={onScheduleClick}
          className="unscheduled-card__action"
        >
          <CalendarIcon className="w-4 h-4" />
          Planifier
        </Button>
        <Button
          variant="secondary"
          size="small"
          onClick={onDetailClick}
          className="unscheduled-card__action"
        >
          <EyeIcon className="w-4 h-4" />
          Détails
        </Button>
      </div>
    </div>
  );
};